import React, { useMemo } from 'react'
import styled from 'styled-components'
import { MAPBOX_TOKEN } from '../../../config'

interface MapboxStaticImageProps {
  lat?: number
  lon?: number
  zoom?: number
  width?: number
  height?: number
  style?: 'streets' | 'satellite' | 'outdoors' | 'light' | 'dark'
  retina?: boolean
  marker?: boolean
  markerColor?: string
  className?: string
  alt?: string
}

const ImageContainer = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const PlaceholderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const MapboxStaticImage: React.FC<MapboxStaticImageProps> = ({
  lat,
  lon,
  zoom = 16,
  width = 300,
  height = 200,
  style = 'satellite',
  retina = true,
  marker = false,
  markerColor = '00ade6',
  className,
  alt = 'Property location',
}) => {
  const imageUrl = useMemo(() => {
    if (!lat || !lon) {
      console.warn('MapboxStaticImage: Missing coordinates', { lat, lon })
      return ''
    }

    if (!MAPBOX_TOKEN) {
      console.warn('Mapbox access token is not configured')
      return ''
    }

    // Map style IDs
    const styleMap: Record<string, string> = {
      streets: 'streets-v12',
      satellite: 'satellite-streets-v12',
      outdoors: 'outdoors-v12',
      light: 'light-v11',
      dark: 'dark-v11',
    }

    const selectedStyle = styleMap[style] || styleMap.satellite
    const retinaParam = retina ? '@2x' : ''

    // Add marker if requested
    const markerParam = marker ? `pin-s+${markerColor}(${lon},${lat})/` : ''

    // Construct the URL
    return `https://api.mapbox.com/styles/v1/mapbox/${selectedStyle}/static/${markerParam}${lon},${lat},${zoom},0/${width}x${height}${retinaParam}?access_token=${MAPBOX_TOKEN}`
  }, [lat, lon, zoom, width, height, style, retina, marker, markerColor])

  if (!imageUrl) {
    return (
      <PlaceholderContainer className={className}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
            fill="currentColor"
            opacity="0.4"
          />
        </svg>
      </PlaceholderContainer>
    )
  }

  return (
    <ImageContainer
      src={imageUrl}
      alt={alt}
      className={className}
      loading="lazy"
      onError={(e) => {
        // Hide broken image icon
        ;(e.target as HTMLImageElement).style.display = 'none'
      }}
    />
  )
}
